import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import { buildFloorPlanSrc } from 'components/marketplace/helpers/galleryPhotos';
import type {
  PresetPictureSet,
  PresetPictureSetOptions,
} from 'components/shared/performance/pictures';
import { generatePresetPictureSetProps, Picture } from 'components/shared/performance/pictures';
import { withImageParams } from 'helpers/imageUrl';

// Keep in sync with Athena `isAcceptableCabinetURL` in
// js/packages/athena/src/clients/cabinetPresizedPhotos.ts.
const S3_HOST_RE = /(?:^|\.)s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
const PASSTHROUGH_HOSTS = new Set(['images.opendoor.com', 'images.simplersell.com']);

export type GalleryPhotoTier = 'md' | 'lg' | 'xl';

export type GalleryBreakpointTier = PresetPictureSet[number] & {
  tier: GalleryPhotoTier;
};

const LISTING_PHOTO_TIER_SPECS = [
  { maxWidth: 1201, preset: '3:2-md', presetScale: '2/3', tier: 'md' },
  { maxWidth: 1400, preset: '3:2-lg', presetScale: '3/4', tier: 'lg' },
  { maxWidth: null, preset: '3:2-xl', presetScale: '1', tier: 'xl' },
] as const;

const LISTING_PHOTO_TIER_MIN_WIDTHS = [1201, 1400] as const;

const buildListingPhotoBreakpointTiers = (firstMinWidth: number): GalleryBreakpointTier[] =>
  LISTING_PHOTO_TIER_SPECS.map((spec, index) => ({
    minWidth: index === 0 ? firstMinWidth : LISTING_PHOTO_TIER_MIN_WIDTHS[index - 1],
    maxWidth: spec.maxWidth,
    preset: spec.preset,
    presetScale: spec.presetScale,
    tier: spec.tier,
  }));

export const MOBILE_CAROUSEL_BREAKPOINT_TIERS = buildListingPhotoBreakpointTiers(0);
export const GALLERY_BREAKPOINT_TIERS = buildListingPhotoBreakpointTiers(768);

const PRESIZED_TIER_FALLBACK_ORDER: Record<GalleryPhotoTier, GalleryPhotoTier[]> = {
  md: ['md', 'lg', 'xl'],
  lg: ['lg', 'md', 'xl'],
  xl: ['xl', 'lg', 'md'],
};

const LARGEST_PRESIZED_TIERS: GalleryPhotoTier[] = PRESIZED_TIER_FALLBACK_ORDER.xl;

export const pickPresizedUrlForGalleryTier = (
  photo: GalleryPhoto,
  tier: GalleryPhotoTier,
): string | undefined => pickFirstPresizedUrl(photo, PRESIZED_TIER_FALLBACK_ORDER[tier]);

export const isCabinetPresizedUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') {
    return false;
  }
  if (S3_HOST_RE.test(parsed.hostname)) {
    return true;
  }
  return PASSTHROUGH_HOSTS.has(parsed.hostname) && parsed.pathname.startsWith('/pre/');
};

export const pickGalleryPhotoUrl = (
  photo: GalleryPhoto,
  tier: GalleryPhotoTier,
): string | null | undefined => {
  if (tier === 'xl') {
    return photo.urlXl ?? photo.urlLg ?? photo.url;
  }
  if (tier === 'lg') {
    return photo.urlLg ?? photo.url;
  }
  return photo.url;
};

export const buildWorkerPhotoUrl = (
  baseUrl: string,
  options: { preset: string; presetScale?: string; dpr?: string; service?: string },
): string =>
  withImageParams(baseUrl, {
    preset: options.preset,
    ...(options.presetScale ? { presetScale: options.presetScale } : {}),
    service: options.service ?? 'cosmos',
    auto: 'format,compress',
    dpr: options.dpr ?? '1',
  });

export const pickFirstPresizedUrl = (
  photo: GalleryPhoto,
  tiers: GalleryPhotoTier[] = LARGEST_PRESIZED_TIERS,
): string | undefined => {
  if (photo.isFloorPlan) {
    return undefined;
  }
  for (const tier of tiers) {
    const candidate = pickGalleryPhotoUrl(photo, tier);
    if (candidate && isCabinetPresizedUrl(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

export const buildGalleryPhotoUrl = (
  photo: GalleryPhoto,
  tierConfig: Pick<GalleryBreakpointTier, 'tier' | 'preset' | 'presetScale'>,
  dpr = '1',
): string => {
  if (photo.isFloorPlan) {
    return buildFloorPlanSrc(photo.url);
  }

  const presized = pickFirstPresizedUrl(photo, PRESIZED_TIER_FALLBACK_ORDER[tierConfig.tier]);
  if (presized) {
    return presized;
  }

  return buildWorkerPhotoUrl(photo.url, {
    preset: tierConfig.preset,
    presetScale: tierConfig.presetScale,
    dpr,
  });
};

export type LightboxPhotoUrls = {
  primary: string;
  thumbnail: string;
};

export const getLightboxPhotoUrls = (photo: GalleryPhoto): LightboxPhotoUrls => {
  if (photo.isFloorPlan) {
    const url = buildFloorPlanSrc(photo.url);
    return { primary: url, thumbnail: url };
  }

  const primary =
    pickFirstPresizedUrl(photo, ['xl', 'lg', 'md']) ??
    buildWorkerPhotoUrl(photo.url, {
      preset: '3:2-lg',
      presetScale: '3/4',
      service: 'lightbox',
    });

  const thumbnail =
    pickFirstPresizedUrl(photo, ['md', 'lg']) ??
    buildWorkerPhotoUrl(photo.url, {
      preset: '3:2-sm',
      presetScale: '1/8',
      service: 'lightbox',
    });

  return { primary, thumbnail };
};

export const generateGalleryPictureSet = (
  props: Omit<PresetPictureSetOptions, 'photoUrl' | 'sizes'> & {
    photo: GalleryPhoto;
    sizes: GalleryBreakpointTier[];
  },
) => {
  const {
    photo,
    sizes,
    lazy,
    preload = false,
    rounded,
    objectFit = 'cover',
    fetchPriority,
    alt,
    debugImageSizes,
    dpr,
    quality,
  } = props;

  const hasPresizedTier = pickFirstPresizedUrl(photo) !== undefined;

  if (hasPresizedTier) {
    // oxlint-disable-next-line unicorn/no-array-sort -- Cosmos still targets browsers without Array.prototype.toSorted.
    const sortedSizes = [...sizes].sort((a, b) => a.minWidth - b.minWidth);
    const sources = sortedSizes.map(({ minWidth, maxWidth, tier }) => {
      const url = pickPresizedUrlForGalleryTier(photo, tier) ?? photo.url;
      const media =
        maxWidth === null
          ? `(min-width: ${minWidth}px)`
          : `(min-width: ${minWidth}px) and (max-width: ${maxWidth - 1}px)`;
      return { srcSet: url, media, key: `${photo.url}-${minWidth}`, fallbackSrc: url };
    });
    const smallestTier = sortedSizes[0]?.tier ?? 'md';
    const fallbackSrc = pickPresizedUrlForGalleryTier(photo, smallestTier) ?? photo.url;

    return (
      <Picture
        key={photo.url}
        preload={preload}
        lazy={lazy ?? true}
        rounded={rounded}
        sources={sources}
        fallbackSrc={fallbackSrc}
        objectFit={objectFit}
        fetchPriority={fetchPriority}
        alt={alt}
      />
    );
  }

  const { sources, fallbackSrc, ...pictureProps } = generatePresetPictureSetProps({
    photoUrl: photo.url,
    sizes,
    lazy,
    preload,
    rounded,
    objectFit,
    fetchPriority,
    alt,
    debugImageSizes,
    dpr,
    quality,
  });

  return <Picture key={photo.url} sources={sources} fallbackSrc={fallbackSrc} {...pictureProps} />;
};
